import WedifyApiRequest from '../../utils/wedify_api_request.js';

export const UserProfileAPI = {
  /**
   * Fetches user profile data by email
   * @param {string} email - User's email address
   * @returns {Promise<Object>} User profile data
   */
  getUserProfile: async (email) => {
    try {
      const wedifyApiRequest = new WedifyApiRequest();
      const headers = {
        'accept': '*/*',
        'accept-language': 'en-US,en;q=0.9',
        'origin': 'http://localhost:3000',
        'email': email
      };
      
      const response = await wedifyApiRequest.makeRequest(
        `${process.env.REACT_APP_API_BASE_URL}/api/login/users/email`,
        "GET",
        null,
        headers
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  },

  /**
   * Fetches user's generated images with pagination
   * @param {string} token - Authentication token
   * @param {Object} params - Request parameters
   * @param {string} params.externalClientId - User's Firebase UID
   * @param {number} params.page - Page number for pagination
   * @returns {Promise<Array>} Array of user's generated images
   */
  getUserImages: async (page) => {
    try {
      const wedifyApiRequest = new WedifyApiRequest();
      const response = await wedifyApiRequest.makeRequest(`${process.env.REACT_APP_API_BASE_URL}/api/images/get/pagination/external/client/id?page=${page}`, "GET");
    
      return await response.json();
    } catch (error) {
      console.error("Error fetching user images:", error);
      throw error;
    }
  },

  /**
   * Fetches user's liked images with pagination
   * @param {string} token - Authentication token
   * @param {Object} params - Request parameters
   * @param {string} params.externalClientId - User's Firebase UID
   * @param {number} params.page - Page number for pagination
   * @returns {Promise<Array>} Array of user's liked images
   */
  getLikedImages: async (token, { externalClientId, page }) => {
    try {
      const wedifyApiRequest = new WedifyApiRequest();
      const response = await wedifyApiRequest.makeRequest(`${process.env.REACT_APP_API_BASE_URL}/api/favorites/get/image/external?page=${page}`, "GET");
      return await response.json();         
    } catch (error) {
      console.error("Error fetching liked images:", error);
      throw error;
    }
  },

  /**
   * Deletes a generated image
   * @param {string} token - Authentication token
   * @param {string} imageId - ID of the image to delete
   * @returns {Promise<Object>} Response from the API
   */
  deleteGeneratedImage: async (token, imageId) => {
    try {
      const wedifyApiRequest = new WedifyApiRequest();
      const response = await wedifyApiRequest.makeRequest(`${process.env.REACT_APP_API_BASE_URL}/api/images/delete/image/${imageId}`, "DELETE");
      return response.status;
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  },

  /**
   * Removes an image from liked images
   * @param {string} token - Authentication token
   * @param {Object} params - Request parameters
   * @param {string} params.externalClientId - User's Firebase UID
   * @param {string} params.imageId - ID of the image to unlike
   * @returns {Promise<Object>} Response from the API
   */
  deleteLikedImage: async (token, { externalClientId, imageId }) => {
    try {
      const wedifyApiRequest = new WedifyApiRequest();
      const response = await wedifyApiRequest.makeRequest(`${process.env.REACT_APP_API_BASE_URL}/api/favorites/delete/external`, "DELETE", {
        imageId,
      });
      return await response.json();
    } catch (error) {
      console.error('Error removing liked image:', error);
      throw error;
    }
  },

  /**
   * Updates user profile information
   * @param {string} externalClientId - User's Firebase UID
   * @param {Object} profileData - Profile data to update
   * @param {string} profileData.person1FirstName - First person's first name
   * @param {string} profileData.person1LastName - First person's last name
   * @param {string} profileData.person2FirstName - Second person's first name
   * @param {string} profileData.person2LastName - Second person's last name
   * @param {string} profileData.weddingDate - Wedding date
   * @param {number} profileData.guestCount - Number of guests
   * @param {string} profileData.weddingLocation - Wedding location
   * @returns {Promise<Object>} Updated user profile data
   */
  updateUserProfile: async (externalClientId, profileData) => {
    try {
      const wedifyApiRequest = new WedifyApiRequest();
      const headers = {
        'External-Client-Id': externalClientId,
        'Authentication-Service': 'firebase'
      };
      
      const response = await wedifyApiRequest.makeRequest(
        `${process.env.REACT_APP_API_BASE_URL}/api/login/users/modify/external`,
        "PUT",
        JSON.stringify(profileData),
        headers
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }
};

export default UserProfileAPI; 